<template>
  <b-modal 
    id="modal-certificate-template" 
    centered 
    hide-footer
    title="Certificate Template"
    size="xl"
    body-class="p-0">
    <div class="modal-container">
      <!-- Alert Info -->
      <div class="alert mb-0 rounded-0 border-0 d-flex align-items-center justify-content-between" style="background-color: #fafafa;">
        <div>
          <i class="fe fe-info text-warning me-2" style="font-size: 1.2rem;"></i>
          <span class="text-dark">You can select the certificate or add new one</span>
        </div>
        <button class="btn btn-danger btn-sm" @click="openAddCertificate">
          <i class="fe fe-plus me-1"></i>
          Add Certificate
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body p-4">
        <!-- Filter Section -->
        <div class="mb-4 pb-3 border-bottom">
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="form-label small">Type</label>
              <v-select
                v-model="filters.type"
                :options="typeOptions"
                placeholder="Select Type"
                @input="onFilterChange"
                :clearable="true">
              </v-select>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label small">Category</label>
              <v-select
                v-model="filters.category"
                :options="categoryOptions"
                label="title"
                :reduce="option => option.id"
                placeholder="Select Category"
                @input="onFilterChange"
                :loading="categoryLoading"
                :clearable="true">
              </v-select>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label small">Search</label>
              <div class="d-flex gap-2">
                <input
                  v-model="filters.search"
                  type="text"
                  class="form-control"
                  placeholder="Search Here..."
                  @input="onSearch">
                <button 
                  class="btn btn-outline-danger" 
                  type="button"
                  @click="clearFilters">
                  <i class="fe fe-x-square"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Header Actions -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5 class="font-weight-bold mb-0">Certificate Result</h5>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-danger btn-sm">
              <i class="fe fe-sliders me-1"></i>
              Filter
            </button>
          </div>
        </div>

        <!-- Certificate Grid -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-danger" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Loading certificates...</p>
        </div>

        <div v-else-if="certificates.length === 0" class="text-center py-5">
          <i class="fe fe-file-text text-muted" style="font-size: 3rem;"></i>
          <p class="mt-3 text-muted">No certificates found</p>
        </div>

        <div v-else class="row">
          <div 
            v-for="cert in certificates" 
            :key="cert.id"
            class="col-md-4 mb-4">
            <div 
              class="certificate-card border rounded p-3 cursor-pointer"
              :class="{ 'selected': selectedCertificate && selectedCertificate.id === cert.id }"
              @click="selectCertificate(cert)">
              <img 
                :src="getCertificateImage(cert)" 
                :alt="cert.name"
                class="img-fluid mb-3"
                style="max-height: 200px; object-fit: cover; width: 100%;">
              <h6 class="mb-2 font-weight-bold">{{ cert.name }}</h6>
              <div class="d-flex gap-2">
                <span class="badge badge-info">{{ cert.type || '' }}</span>
                <span class="badge badge-warning">{{ cert.training_category.title || '' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="mt-4 pt-3 border-top" v-if="totalItems > 0">
          <div class="row align-items-center">
            <div class="col-md-4">
              <div class="d-flex align-items-center">
                <span class="text-muted small me-2">Show</span>
                <select 
                  v-model="perPage" 
                  class="form-select form-select-sm" 
                  style="width: 70px;"
                  @change="onPerPageChange">
                  <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                </select>
                <span class="text-muted small ms-2">entries</span>
              </div>
            </div>
            <div class="col-md-4 text-center">
              <small class="text-muted">
                Showing {{ totalItems > 0 ? (currentPage - 1) * perPage + 1 : 0 }} to {{ Math.min(currentPage * perPage, totalItems) }} of {{ totalItems }} entries
              </small>
            </div>
            <div class="col-md-4">
              <nav>
                <ul class="pagination pagination-sm justify-content-end mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <button class="page-link" @click="previousPage" :disabled="currentPage === 1">
                      <i class="fe fe-chevron-left"></i>
                    </button>
                  </li>
                  <li 
                    v-for="page in visiblePages" 
                    :key="page"
                    class="page-item" 
                    :class="{ active: currentPage === page, disabled: page === '...' }">
                    <button 
                      v-if="page === '...'" 
                      class="page-link" 
                      disabled>
                      ...
                    </button>
                    <button 
                      v-else
                      class="page-link" 
                      @click="goToPage(page)">
                      {{ page }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages || totalPages === 0 }">
                    <button class="page-link" @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0">
                      <i class="fe fe-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer border-top">
        <button class="btn btn-danger px-4" @click="confirmSelection">
          OK
        </button>
      </div>
    </div>

    <!-- Add Certificate Modal -->
    <modal-add-certificate @certificate-added="onCertificateAdded" />
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ModalAddCertificate from './ModalAddCertificate.vue';

export default {
  name: 'ModalCertificateTemplate',
  components: {
    ModalAddCertificate
  },
  data() {
    return {
      selectedCertificate: null,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100],
      typeOptions: ['Internal', 'External', 'Thematic'],
      categoryOptions: [],
      categoryLoading: false,
      filters: {
        type: null,
        category: null,
        search: ''
      },
      searchTimeout: null
    }
  },

  computed: {
    ...mapState('certificate', {
      certificates: state => state.certificates,
      totalItems: state => state.totalItems,
      loading: state => state.loading
    }),

    totalPages() {
      return Math.ceil(this.totalItems / this.perPage) || 0;
    },

    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages.length > 0 ? pages : [1];
    }
  },

  mounted() {
    this.loadCertificates();
    this.loadCategories();

    // Listen for modal shown event to reload data
    this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
      if (modalId === 'modal-certificate-template') {
        this.loadCertificates();
        this.loadCategories();
      }
    });
  },

  beforeDestroy() {
    this.$root.$off('bv::modal::shown');
  },
  methods: {
    ...mapActions('certificate', ['getCertificates']),
    ...mapActions('trainingCategory', ['getTrainingCategories']),

    async loadCategories() {
      try {
        this.categoryLoading = true;
        const response = await this.getTrainingCategories({ page: 1, limit: 100 });
        
        if (response.data && response.data.data) {
          this.categoryOptions = response.data.data;
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        this.categoryLoading = false;
      }
    },

    async loadCertificates() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage
        };

        // Add filters if set
        if (this.filters.type) {
          params.type = this.filters.type;
        }
        if (this.filters.category) {
          params.training_category_id = this.filters.category;
        }
        if (this.filters.search) {
          params.search = this.filters.search;
        }

        await this.getCertificates(params);
      } catch (error) {
        console.error('Error loading certificates:', error);
        this.$swal('Error', 'Sertifikat belum berhasil dimuat', 'error');
      }
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadCertificates();
    },

    onSearch() {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadCertificates();
      }, 500);
    },

    clearFilters() {
      this.filters = {
        type: null,
        category: null,
        search: ''
      };
      this.currentPage = 1;
      this.loadCertificates();
    },

    closeModal() {
      this.$bvModal.hide('modal-certificate-template');
      this.selectedCertificate = null;
    },

    openAddCertificate() {
      this.$bvModal.show('modal-add-certificate');
    },

    onCertificateAdded() {
      // Reload certificates after adding new one
      this.loadCertificates();
      this.$swal('Success', 'Sertifikat berhasil ditambahkan', 'success');
    },

    selectCertificate(cert) {
      this.selectedCertificate = cert;
    },

    getCertificateImage(cert) {
      if (cert.image_url) {
        return `${this.imageBaseUrl}/attachments/${cert.image_url}`;
      }
      return 'https://dummyimage.com/400x300/e9ecef/6c757d&text=No+Image';
    },

    confirmSelection() {
      if (this.selectedCertificate) {
        // Only emit certificate_id
        this.$emit('certificate-selected', {
          certificate_id: this.selectedCertificate.id,
          certificate_name: this.selectedCertificate.name,
          certificate_image: this.getCertificateImage(this.selectedCertificate)
        });
        this.closeModal();
      } else {
        this.$swal("Warning", "Pilih sertifikat", "warning");
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadCertificates();
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadCertificates();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadCertificates();
      }
    },

    onPerPageChange() {
      this.currentPage = 1;
      this.loadCertificates();
    }
  }
}
</script>

<style scoped>
.modal-container {
  min-height: 400px;
}

.btn-close {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-close:hover {
  opacity: 1;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffecb5;
  color: #856404;
}

.certificate-card {
  transition: all 0.3s;
  position: relative;
}

.certificate-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.certificate-card.selected {
  border: 3px solid #e63757 !important;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

/* .certificate-card.selected::after {
  content: '✓';
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #e63757;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
} */

.cursor-pointer {
  cursor: pointer;
}

.badge {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.375rem;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}

/* Pagination */
.pagination {
  margin-bottom: 0;
}

.page-link {
  color: #6c757d;
  border-color: #dee2e6;
  padding: 0.5rem 0.75rem;
}

.page-link:hover {
  color: #e63757;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.page-item.active .page-link {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}
</style>
